/**
 * Detail pane of the Findings queue (Enter). The list keeps its place;
 * the pane shows the finding, why it ranks where it does, the evidence
 * and the triage actions. On phones the parent renders it in a sheet.
 */
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, X } from "lucide-react";
import { FindingKicker } from "@/components/experience/finding-kicker";
import type { FindingType, Severity } from "@/components/experience/vocabulary";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Chip } from "@/components/ui/chip";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { Skeleton } from "@/components/ui/skeleton";
import { Timestamp } from "@/components/ui/timestamp";
import { trpc } from "@/lib/trpc";
import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import { FindingObservation } from "@/components/experience/finding-observation";
import { ExportFindingButton } from "@/components/experience/finding-actions";
import { useCanEdit } from "@/lib/project-access";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import { linkNoun, linkStateChip } from "@/components/workflow/workflow-labels";
import type { ProjectExportConfig } from "@iamu/shared";
import {
  resolveFindingTitle,
  resolveFindingDescription,
} from "@/lib/finding-resolution";
import { PriorityChip, PriorityExplanation } from "./priority-chip";
import { QueueDetailEvidence } from "./queue-detail-evidence";
import { QueueTriageControls } from "./queue-triage-controls";
import type { QueueRowData } from "./queue-types";

interface QueueDetailPaneProps {
  projectId: string;
  row: QueueRowData;
  /** Triage buttons (the same actions as the keys). */
  actions: ReactNode;
  onClose?: () => void;
  exportConfig?: ProjectExportConfig;
  hasLinkedRepo?: boolean;
  onOpenZen?: () => void;
}

export function QueueDetailPane({
  projectId,
  row,
  actions,
  onClose,
  exportConfig,
  hasLinkedRepo,
  onOpenZen,
}: QueueDetailPaneProps) {
  const location = useLocation();
  const { data: detail, isLoading } = trpc.finding.getById.useQuery({
    id: row.id,
  });
  const { data: workflowData } = trpc.workflow.get.useQuery(
    { canonicalId: row.id },
    { staleTime: 30_000 },
  );
  const canEdit = useCanEdit();
  const observations =
    detail && "observations" in detail
      ? (detail.observations as Array<{
          id: string;
          walkthroughId: string;
          stepNumber?: number | null;
          stepTitle?: string | null;
          personaReasoning?: string | null;
          surface?: string | null;
          pageUrl?: string | null;
          severity: string | null;
          createdAt: string;
          targetBox?: TargetBox | null;
        }>)
      : [];
  const description = resolveFindingDescription(detail as any);
  const uiFact =
    detail && "effectiveUiFact" in detail
      ? (detail.effectiveUiFact as string | null)
      : null;
  const consequence =
    detail && "effectiveConsequence" in detail
      ? (detail.effectiveConsequence as string | null)
      : null;
  const what =
    detail && "effectiveWhat" in detail
      ? (detail.effectiveWhat as string | null)
      : null;
  const why =
    detail && "effectiveWhy" in detail
      ? (detail.effectiveWhy as string | null)
      : null;
  const basis =
    detail && "effectiveBasis" in detail
      ? (detail.effectiveBasis as string | null)
      : null;
  const screen =
    detail && "effectiveScreen" in detail
      ? (detail.effectiveScreen as string | null)
      : (row.screen ?? null);
  const breaksRule =
    detail && "effectiveBreaksRule" in detail
      ? (detail.effectiveBreaksRule as string | null)
      : (row.breaksRule ?? null);
  const breaksFile =
    detail && "effectiveBreaksFile" in detail
      ? (detail.effectiveBreaksFile as string | null)
      : (row.breaksFile ?? null);
  const recommendation =
    detail && "effectiveRecommendation" in detail
      ? (detail.effectiveRecommendation as string | null)
      : null;
  const title = resolveFindingTitle((detail ?? row) as any, "Untitled finding");

  return (
    <article aria-labelledby="queue-detail-title" className="space-y-5">
      <header className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <PriorityChip canonicalId={row.id} priority={row} />
            <FindingKicker
              severity={row.severity as Severity | null}
              type={row.type as FindingType | null}
              state={row.state}
              stateReason={row.stateReason}
            />
          </div>
          <div className="flex items-center gap-1">
            <SimpleTooltip content="Open full finding page">
              <Button
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                asChild
              >
                <Link
                  to={`/projects/${projectId}/findings/${row.id}`}
                  aria-label="Open full finding page"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </SimpleTooltip>
            {onClose && (
              <SimpleTooltip content="Close (Esc)">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  aria-label="Close the detail pane"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </SimpleTooltip>
            )}
          </div>
        </div>
        <h2 id="queue-detail-title" className="text-lg font-semibold">
          {title}
        </h2>
        <p className="text-xs text-muted-foreground">
          {[row.outlineName, row.personaName].filter(Boolean).join(" · ")}
          {row.lastSeenAt ? (
            <>
              {row.outlineName || row.personaName ? " · " : ""}
              Last seen <Timestamp date={row.lastSeenAt} />
            </>
          ) : null}
        </p>
        {workflowData?.links && workflowData.links.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {workflowData.links.map((link) => {
              const chip = linkStateChip(link);
              return (
                <a
                  key={link.id}
                  href={link.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium hover:bg-muted transition-colors"
                >
                  <ProviderIcon provider={link.provider} className="size-3.5" />
                  <span>
                    {linkNoun(link.kind)} {link.displayKey ?? ""}
                  </span>
                  <Chip tone={chip.tone} className="text-2xs py-0 px-1.5 h-4">
                    {chip.label}
                  </Chip>
                  {link.url && (
                    <ExternalLink className="size-3 text-muted-foreground" />
                  )}
                </a>
              );
            })}
          </div>
        )}
      </header>

      <QueueTriageControls
        projectId={projectId}
        row={row}
        canEdit={canEdit}
        workflowData={workflowData}
        actions={actions}
      />

      {row.stateNote && (
        <p className="text-sm">
          <span className="font-medium">Note: </span>
          {row.stateNote}
        </p>
      )}

      <QueueDetailEvidence
        projectId={projectId}
        detail={detail}
        row={row}
        observations={observations}
        onOpenZen={onOpenZen}
      />

      <section
        className="space-y-2 border-t border-border pt-4"
        aria-label="What happened"
      >
        <h3 className="text-sm font-semibold text-foreground">Observation</h3>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <FindingObservation
            uiFact={uiFact}
            consequence={consequence}
            what={what}
            why={why}
            basis={basis}
            screen={screen}
            breaksRule={breaksRule}
            breaksFile={breaksFile}
            description={description ?? row.preview}
          />
        )}
      </section>

      {recommendation && (
        <section
          className="space-y-2 border-t border-border pt-4"
          aria-label="Recommendation"
        >
          <h3 className="text-sm font-semibold text-foreground">
            Recommendation
          </h3>
          <MarkdownProse content={recommendation} lineBreaks />
        </section>
      )}

      {row.priorityScore !== null && (
        <section
          className="space-y-2 border-t border-border pt-4"
          aria-label="Why it ranks here"
        >
          <h3 className="text-sm font-semibold text-foreground">
            Why it ranks here
          </h3>
          <PriorityExplanation canonicalId={row.id} />
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
        <Link
          to={`/projects/${projectId}/findings/${row.id}`}
          state={{
            from: location.pathname + location.search,
            label: "Queue",
          }}
          className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
        >
          Open the full finding page
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>

        {exportConfig && (
          <ExportFindingButton
            findingId={row.id}
            projectId={projectId}
            exportConfig={exportConfig}
            hasLinkedRepo={Boolean(hasLinkedRepo)}
          />
        )}
      </div>
    </article>
  );
}
